// src/hooks/useRandomScrollPromo.js
import { useEffect, useRef } from "react";
import { getLocalDateKey } from "../utils/dateKey";

/**
 * Random, rate-limited promo modal trigger on scroll.
 * Works in JSX (no TypeScript).
 */
export function useRandomScrollPromo(options) {
  const {
    onTrigger,                 // required: () => void
    storageKeyBase = "promo_random",
    minTimeMs = 15000,
    minScrollPx = 600,
    probability = 0.08,
    checkThrottleMs = 1500,
    cooldownMs = 30 * 60 * 1000, // 30 minutes
    dailyCap = 1,
    sessionCap = 1,
    debug = false,
    // If your app scrolls in a container, pass a ref or element here.
    // Otherwise, omit (defaults to window).
    scrollTarget = typeof window !== "undefined" ? window : undefined,
  } = options || {};

  const startedAt = useRef(Date.now());
  const lastCheckRef = useRef(0);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (typeof onTrigger !== "function") return;

    const targetEl =
      scrollTarget && scrollTarget.current ? scrollTarget.current : scrollTarget || window;

    const todayKey = `${storageKeyBase}_day`;
    const countKey = `${storageKeyBase}_count`;
    const cooldownKey = `${storageKeyBase}_cooldown_until`;
    const sessionShownKey = `${storageKeyBase}_session_shown`;
    const sessionDayKey = `${storageKeyBase}_session_day`;

    const log = (...args) => debug && console.log("[promo]", storageKeyBase, ...args);

    // --- DAILY ROLLOVER (idempotent) ---
    const rollDailyIfNeeded = () => {
      const today = getLocalDateKey();                 // local YYYY-MM-DD

      // localStorage (daily bucket)
      const storedDay = localStorage.getItem(todayKey);
      if (storedDay !== today) {
        localStorage.setItem(todayKey, today);
        localStorage.setItem(countKey, "0");
        // Optional: fresh chance each day:
        // localStorage.removeItem(cooldownKey);
      }

      // sessionStorage (per-tab session bucket)  <-- NEW
      const sessDay = sessionStorage.getItem(sessionDayKey);
      if (sessDay !== today) {
        sessionStorage.setItem(sessionDayKey, today);
        sessionStorage.setItem(sessionShownKey, "0");  // reset per-session count for the new day
      }
    };

    // Initialize on mount (so localStorage immediately shows the correct date)
    rollDailyIfNeeded();

    const incDailyCount = () => {
      rollDailyIfNeeded();
      const n = parseInt(localStorage.getItem(countKey) || "0", 10) + 1;
      localStorage.setItem(countKey, String(n));
      log("increment daily count", { n });
    };

    const canShow = () => {
      rollDailyIfNeeded();

      // session cap
      const sessionCount = parseInt(sessionStorage.getItem(sessionShownKey) || "0", 10);
      if (sessionCount >= sessionCap) {
        log("blocked: session cap", { sessionCount, sessionCap });
        return false;
      }

      // daily cap
      const todayCount = parseInt(localStorage.getItem(countKey) || "0", 10);
      if (todayCount >= dailyCap) {
        log("blocked: daily cap", { todayCount, dailyCap });
        return false;
      }

      // cooldown
      const until = parseInt(localStorage.getItem(cooldownKey) || "0", 10);
      if (Date.now() < until) {
        log("blocked: cooldown", { now: Date.now(), until });
        return false;
      }

      return true;
    };

    const markShown = () => {
      localStorage.setItem(cooldownKey, String(Date.now() + cooldownMs));
      const sessionCount = parseInt(sessionStorage.getItem(sessionShownKey) || "0", 10) + 1;
      sessionStorage.setItem(sessionShownKey, String(sessionCount));
      incDailyCount();
    };

    const getScrollPos = () => {
      return targetEl === window ? window.scrollY : (targetEl && targetEl.scrollTop) || 0;
    };

    const eligibleNow = () => {
      if (triggeredRef.current) return false;
      if (!canShow()) return false;
      if (Date.now() - startedAt.current < minTimeMs) {
        log("blocked: minTime", { elapsed: Date.now() - startedAt.current, minTimeMs });
        return false;
      }
      const scrollPos = getScrollPos();
      if (scrollPos < minScrollPx) {
        log("blocked: minScroll", { scrollPos, minScrollPx, target: targetEl === window ? "window" : "element" });
        return false;
      }
      return true;
    };

    const maybeTrigger = () => {
      if (!eligibleNow()) return;
      const r = Math.random();
      log("maybeTrigger()", { r, probability });
      if (r < probability) {
        triggeredRef.current = true;
        markShown();
        log("TRIGGER");
        onTrigger();
      }
    };

    const onScroll = () => {
      const now = Date.now();
      if (now - lastCheckRef.current < checkThrottleMs) return;
      lastCheckRef.current = now;
      requestAnimationFrame(maybeTrigger);
    };

    // --- NEW: Visibility change -> roll date when tab becomes active ---
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        rollDailyIfNeeded();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // --- Local midnight rollover for long-lived sessions ---
    const scheduleMidnightReset = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      const ms = nextMidnight.getTime() - now.getTime();
      return window.setTimeout(() => {
        rollDailyIfNeeded();
        log("midnight rollover");
        scheduleMidnightReset(); // re-arm
      }, ms);
    };
    const midnightTimer = scheduleMidnightReset();

    // --- NEW: lightweight heartbeat so date updates even without scroll ---
    const heartbeatTimer = window.setInterval(rollDailyIfNeeded, 60 * 1000); // every 60s

    // One-time delayed check (in case user doesn’t scroll)
    const initialTimer = window.setTimeout(maybeTrigger, Math.max(0, minTimeMs + 300));

    // Listen to the correct scroll target
    if (targetEl && targetEl.addEventListener) {
      targetEl.addEventListener("scroll", onScroll, { passive: true });
    } else {
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    window.addEventListener("touchmove", onScroll, { passive: true });

    // Keep buckets in sync across tabs/windows using the same base
    const onStorage = (e) => {
      if (!e.key) return;
      if (e.key.startsWith(storageKeyBase)) {
        rollDailyIfNeeded();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(midnightTimer);
      clearInterval(heartbeatTimer);
      if (targetEl && targetEl.removeEventListener) {
        targetEl.removeEventListener("scroll", onScroll);
      } else {
        window.removeEventListener("scroll", onScroll);
      }
      window.removeEventListener("touchmove", onScroll);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    onTrigger,
    storageKeyBase,
    minTimeMs,
    minScrollPx,
    probability,
    checkThrottleMs,
    cooldownMs,
    dailyCap,
    sessionCap,
    debug,
    scrollTarget,
  ]);
}
